import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import type { ComponentProps } from '../ComponentProps';
import { getClassNameOverride } from '../getClassNameOverride';
import type { InputRounded, InputSize, InputState, InputType } from './types';

export interface InputProps
  extends ComponentProps<Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>> {
  type?: InputType;
  size?: InputSize;
  rounded?: InputRounded;
  value?: string | number;
  state?: keyof typeof InputState;
}

const input = variants({
  base: [
    'w-full',
    'outline',
    'outline-1',
    'outline-emphasis-subtle',
    'outline-offset-[-1px]',
    'focus:ring-none',
    'text-body-sm',
    'py-md',
    'px-md',
    'bg-emphasis-default',
    'text-strong',
    'placeholder:text-subtle',
    'resize-none',
    'disabled:cursor-not-allowed',
    'disabled:outline-disabled',
    'disabled:outline-opacity-subtle',
    'disabled:outline-px',
    'disabled:text-disabled',
    'read-only:cursor-not-allowed',
    'read-only:outline-disabled',
    'read-only:outline-opacity-subtle',
    'read-only:outline-px',
    'read-only:text-disabled',
    'enabled:hover:outline-opacity-stronger',
    // Workaround for Chrome autofill forced styles that can't be overridden normally.
    // The 5000s transition delays the browser's background for a long time.
    // See: https://stackoverflow.com/q/71640408
    'autofill:[-webkit-text-fill-color:oklch(var(--text-default))]',
    'autofill:[caret-color:oklch(var(--text-default))]',
    'autofill:[-webkit-box-shadow:0_0_0_1000px_transparent_inset]',
    'autofill:[transition:background-color_5000s_ease-in-out_0s]',
  ],
  variants: {
    state: {
      default: [
        'focus:outline-secondary-default',
        'hover:outline-disabled',
        'focus:outline-2',
        'hover:outline-2',
        'hover:outline-offset-[-2px]',
        'focus:outline-offset-[-2px]',
      ],
      error: [
        'outline',
        'outline-error-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
      warning: [
        'outline',
        'outline-warning-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
      success: [
        'outline',
        'outline-success-default',
        'outline-2',
        'outline-offset-[-2px]',
      ],
    },
    size: {
      xlarge: ['leading-6'],
      large: ['leading-4'],
    },
    rounded: {
      xsmall: ['rounded-xs'],
      small: ['rounded-sm'],
      medium: ['rounded-md'],
    },
  },
});

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { type, size = 'large', rounded = 'xsmall', state = 'default', ...props },
    ref,
  ) => {
    const stateClasses = input({
      state,
      rounded,
      size,
    });

    const className = getClassNameOverride(props, 'Input');

    return (
      <input
        aria-invalid={state === 'error'}
        className={cn(stateClasses, className)}
        type={type}
        ref={ref}
        {...props}
      />
    );
  },
);

Input.displayName = 'Input';

export { Input };
